import { client } from './api';

/**
 * Draft referrals: gathering a packet before the patient exists.
 *
 * A draft is not an intake and nothing else in the app treats it as one. It
 * holds a name somebody typed and the faxes they have found so far, and it
 * stops existing the moment those become a real referral.
 *
 * Hand written against the client like the fax inbox's other calls, so this
 * screen's API surface stays in one file and a client regeneration does not
 * drag unrelated routes into the diff.
 */

export interface IntakeDraft {
  id: string;
  name: string;
  document_count: number;
  created_at: string;
  created_by_name: string | null;
}

export interface IntakeDraftDocument {
  kind: 'arrival' | 'document';
  id: string;
  name: string;
  page_count: number | null;
  received_at: string | null;
}

export interface IntakeDraftDetail {
  draft: IntakeDraft;
  documents: IntakeDraftDocument[];
}

export interface StartedReferral {
  intake_id: string;
  document_count: number;
}

interface DraftList {
  items: IntakeDraft[];
}

export async function listIntakeDrafts(
  search?: string
): Promise<IntakeDraft[]> {
  const response = await client.get<DraftList, unknown, true>({
    query: { search },
    responseType: 'json',
    throwOnError: true,
    url: '/intake-drafts',
  });
  return response.data.items;
}

/**
 * Start a draft under the name somebody searched for.
 *
 * A name an open draft already uses comes back as that draft rather than an
 * error, so two people working the same referral land on one packet instead
 * of each building half of it.
 */
export async function createIntakeDraft(name: string): Promise<IntakeDraft> {
  const response = await client.post<IntakeDraft, unknown, true>({
    body: { name },
    responseType: 'json',
    throwOnError: true,
    url: '/intake-drafts',
  });
  return response.data;
}

export async function getIntakeDraft(
  draftId: string
): Promise<IntakeDraftDetail> {
  const response = await client.get<IntakeDraftDetail, unknown, true>({
    responseType: 'json',
    throwOnError: true,
    url: `/intake-drafts/${draftId}`,
  });
  return response.data;
}

export async function renameIntakeDraft(
  draftId: string,
  name: string
): Promise<IntakeDraft> {
  const response = await client.put<IntakeDraft, unknown, true>({
    body: { name },
    responseType: 'json',
    throwOnError: true,
    url: `/intake-drafts/${draftId}`,
  });
  return response.data;
}

/**
 * Abandon a draft.
 *
 * The faxes gathered under it go back to the inbox rather than staying filed
 * against something that no longer exists.
 */
export async function removeIntakeDraft(draftId: string): Promise<void> {
  await client.delete<void, unknown, true>({
    responseType: 'json',
    throwOnError: true,
    url: `/intake-drafts/${draftId}`,
  });
}

/**
 * Turn a draft into a real referral.
 *
 * The gathered faxes are already known to the server, so only the documents
 * somebody added in the dialog travel with the request. An empty body is the
 * normal case: most drafts are made entirely of faxes.
 */
export async function startReferralFromDraft(
  draftId: string,
  files: File[] = []
): Promise<StartedReferral> {
  const body = new FormData();
  for (const file of files) {
    body.append('files', file);
  }
  const response = await client.post<StartedReferral, unknown, true>({
    body,
    // Left for the browser to set, because a multipart body needs the boundary
    // the browser generates and naming the type by hand drops it.
    headers: { 'Content-Type': null },
    responseType: 'json',
    throwOnError: true,
    url: `/intake-drafts/${draftId}/referral`,
  });
  return response.data;
}
